import React, { useEffect, useState } from "react";
import AllVendorCard from "./AllVendorCard";
import { DNF } from "../../../../images/Images";
import "./AllVendors.css";
import ButtonSvg from "./component/ButtonSvg";
import SmallAddSvg from "./svgs/SmallAddSvg";
import FilterSvg from "./svgs/FilterSvg";
import AddVendorModal from "../../components/modal/AddVendorModal";
import VendorDetailsModal from "../../components/modal/VendorDetailsModal";
import FilterVendorModal from "./component/FilterVendorModal";
import axiosInstance from "../../../../axiosInstance";
import LoadingComponent from "../../../../components/loading/Loading";
import Joyride, { Step, CallBackProps, STATUS } from "react-joyride";

// Define the Vendor type based on your API response
type Vendor = {
  id: string;
  venture: {
    id: string;
    name: string;
    tagline: string;
    description: string;
    email: string;
    phone: string;
    tax_id: string;
    logo: string | null;
    is_verified: boolean;
    stripe_account_id: string | null;
    payment_status: string;
    can_receive_payments: boolean;
  };
  user: {
    id: string;
    full_name: string;
    email: string;
    phone_number: string;
    is_verified_email: boolean;
    membership_type: string;
    role: string;
    avatar: string;
  };
};

const steps: Step[] = [
  {
    target: ".add_vendor",
    content: "Click here to add your first vendor.",
    placement: "bottom",
    disableBeacon: true,
  },
  {
    target: ".navs__link-orders",
    content: "Track your orders here.",
    placement: "bottom",
    disableBeacon: true,
  },
  {
    target: ".navs__link-cart",
    content: "View your cart here.",
    placement: "bottom",
    disableBeacon: true,
  },
];

const AllVendors: React.FC = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isVendorDetailsOpen, setIsVendorDetailsOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [originalVendors, setOriginalVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isFilterVendorModalOpen, setIsFilterVendorModalOpen] = useState(false);

  const handleAddFilter = (filters: { contact: string }) => {
    console.log("Filters received:", filters);
    // Process the data accordingly
  };

  const getUserStatus = async () => {
    try {
      const res = await axiosInstance.get("/auth/me");
      const isVerified = res.data.data.is_verified_email;
      const userEmail = res.data.data.email;
      const userId = res.data.data.id;

      localStorage.removeItem("verify_username");
      localStorage.setItem("verify_status", isVerified ? "true" : "false");
      localStorage.setItem("verify_id", userId);

      if (!isVerified) {
        // Redirect to verify email page if not verified
        window.location.href = "/verify-email";
        return false;
      }
      return true;
    } catch (error) {
      localStorage.setItem("verify_status", "false");
      window.location.href = "/verify-email";
      return false;
    }
  };

  const handleVendorFilter = (filters: {
    category: string;
    vendorName: string;
    location: string;
    vendorId: string;
  }) => {
    let filtered = originalVendors;

    if (filters.category) {
      filtered = filtered.filter((v) =>
        v.venture.tagline
          ?.toLowerCase()
          .includes(filters.category.toLowerCase())
      );
    }
    if (filters.vendorName) {
      filtered = filtered.filter((v) =>
        v.venture.name?.toLowerCase().includes(filters.vendorName.toLowerCase())
      );
    }
    if (filters.location) {
      filtered = filtered.filter((v) =>
        v.venture.description
          ?.toLowerCase()
          .includes(filters.location.toLowerCase())
      );
    }
    if (filters.vendorId) {
      filtered = filtered.filter((v) => v.venture.id === filters.vendorId);
    }

    setFilteredVendors(filtered);
  };

  const allVendors = async () => {
    try {
      const response = await axiosInstance.get("/ventures/selections");
      setVendors(response.data.data);
      setOriginalVendors(response.data.data);
      setFilteredVendors(response.data.data);
      setLoading(false);
      setShowTour(response.data.data.length === 0);
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setLoading(false);
      setShowTour(true);
    }
  };

  // This function will be called after a vendor is successfully added
  const handleVendorAdded = (newVendor: Vendor) => {
    setIsAddModalOpen(false); // Close the add modal
    setSelectedVendor(newVendor); // Set the new vendor
    setIsVendorDetailsOpen(true); // Open the details modal
    allVendors(); // Optionally refetch vendors
  };

  // Joyride callback to hide tour when finished/skipped
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || STATUS.SKIPPED) {
      setShowTour(false);
    }
  };

  useEffect(() => {
    allVendors();
    getUserStatus();
  }, []);

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <>
      {showTour && (
        <Joyride
          steps={steps}
          continuous
          showSkipButton
          run={true}
          callback={handleJoyrideCallback}
          styles={{
            options: {
              zIndex: 9999,
              primaryColor: "#089B14",
            },
            buttonNext: {
              backgroundColor: "#089B14",
              color: "#fff",
            },
            buttonBack: {
              color: "#089B14",
            },
          }}
          locale={{
            last: "Finish",
            next: "Next",
            skip: "Skip",
            back: "Back",
            close: "Close",
          }}
        />
      )}
      <div className="all_vendors_header">
        <h3>Vendors</h3>
        <div onClick={() => setIsAddModalOpen(true)} className="add_vendor">
          <ButtonSvg icon={<SmallAddSvg />} label="Add" />
        </div>
        <div onClick={() => setIsFilterVendorModalOpen(true)}>
          <ButtonSvg icon={<FilterSvg />} label="filter" />
        </div>
      </div>

      <div className="all_vendors">
        {filteredVendors.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <img src={DNF} alt="No vendors" style={{}} />
            {/* <div
              style={{
                fontWeight: 800,
                fontSize: 18,
                color: "#444",
                marginBottom: 12,
                textAlign: "center",
                position: "absolute",
                bottom: "10%",
              }}
            >
              No vendor found
            </div> */}
            {/* <div
              onClick={() => setIsAddModalOpen(true)}
              className="add_vendor"
              style={{ position: "absolute", bottom: 10 }}
            >
              <ButtonSvg icon={<SmallAddSvg />} label="Add Vendor" />
            </div> */}
          </div>
        ) : (
          filteredVendors.map((vendor) => (
            <AllVendorCard
              key={vendor.id}
              image={
                vendor.venture.logo ||
                "https://img.freepik.com/free-photo/two-women-viewing-content-phone-local-african-market_181624-35124.jpg?uid=R27806234&ga=GA1.1.1994711177.1748430137&semt=ais_items_boosted&w=740"
              }
              name={vendor.venture.name}
              category={vendor.venture.tagline}
              price={"$0"}
              vendor={vendor.venture.name}
              description={vendor.venture.description}
              reviews={0}
              avatar={vendor?.user?.avatar}
              userId={vendor.venture.id}
            />
          ))
        )}
      </div>

      <FilterVendorModal
        isOpen={isFilterVendorModalOpen}
        onClose={() => setIsFilterVendorModalOpen(false)}
        onFilter={handleVendorFilter}
      />

      <AddVendorModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onFilter={handleAddFilter}
        onVendorAdded={handleVendorAdded}
        vendors={vendors} // Pass the vendor list
      />

      {isVendorDetailsOpen && selectedVendor && (
        <VendorDetailsModal
          isOpen={isVendorDetailsOpen}
          onClose={() => setIsVendorDetailsOpen(false)}
          vendor={selectedVendor}
          vendors={vendors}
        />
      )}
    </>
  );
};

export default AllVendors;
